import type { AxiosRequestConfig } from 'axios';

import config from '../../config';
import { Api, type ApiConfig } from '../api';
import { BUSTLY_CORE_PRODUCT_CODE, PRODUCT_CONTEXT_HEADER } from '../productContext';

export type MarketCoverageMode = 'estimated_open_world' | 'enumerated';
export type MarketCadence = 'daily' | 'twice_daily' | 'weekly';
export type MarketMediaType = 'all' | 'image' | 'video';
export type MarketQuerySliceSource = 'meta' | 'tiktok' | 'reddit' | 'reddit_comments' | 'x';
export type MarketQueryStatus = 'accepted' | 'running' | 'ready' | 'partial' | 'failed';
export type MarketExecutionMode = 'fake' | 'provider';
export type MarketRuntimeExecutionMode = 'disabled' | MarketExecutionMode;
export type MarketRuntimeState = 'ready' | 'blocked' | 'unavailable';

export interface MarketRuntimeStatus {
    status: MarketRuntimeState;
    execution_mode: MarketRuntimeExecutionMode | null;
    can_run: boolean;
    reason_code: string;
}
export type MarketCapabilityAvailability = 'enabled' | 'disabled' | 'degraded';
export interface MarketCapabilityScope {
    mode: string;
    values: string[];
}
export interface MarketCapability {
    capability: string;
    platform: 'meta' | 'tiktok' | 'reddit' | 'x';
    source_system: string;
    availability: MarketCapabilityAvailability;
    execution_mode: MarketRuntimeExecutionMode | null;
    reason_code: string;
    rollout_stage: string;
    region_scope: MarketCapabilityScope;
    language_scope: MarketCapabilityScope;
}
export type MarketWatchTargetType = 'brand' | 'product' | 'ad_creative';
export type MarketHeatStage =
    | 'baseline_building'
    | 'insufficient_data'
    | 'stable'
    | 'emerging'
    | 'rising'
    | 'peaking'
    | 'cooling';
export type MarketHeatWindow = '24h' | '7d' | '30d';

export interface MarketQuerySliceWrite {
    slice_id: string;
    source: MarketQuerySliceSource;
    query: string;
    active_status: 'active';
    media_type: MarketMediaType;
    stratum: string;
    days_back?: number;
}

export interface MarketMonitorWrite {
    contract_version: '1.0.0';
    name: string;
    enabled: boolean;
    watch_target_id: string | null;
    coverage_mode: MarketCoverageMode;
    region: string;
    category: string;
    advertiser_page_urls: string[];
    query_slices: MarketQuerySliceWrite[];
    cadence: MarketCadence;
    max_provider_records_per_run: number;
}

export interface MarketObservationPlan {
    plan_id: string;
    version: number;
    coverage_mode: MarketCoverageMode;
    planned_slices: number;
    expected_targets: number | null;
    effective_from: string;
}

export interface MarketMonitor {
    contract_version: '1.0.0';
    monitor_id: string;
    name: string;
    enabled: boolean;
    watch_target_id: string | null;
    coverage_mode: MarketCoverageMode;
    region: string;
    category: string;
    cadence: MarketCadence;
    advertiser_page_urls: string[];
    query_slices: MarketQuerySliceWrite[];
    max_provider_records_per_run: number;
    version: number;
    current_plan: MarketObservationPlan;
    last_success_at: string | null;
    next_run_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface MarketMonitorPreview {
    normalized_monitor_spec: Record<string, unknown>;
    planned_slices: number;
    max_provider_records: number;
    warnings: string[];
}

export interface MarketTargetAccounting {
    planned_targets?: number | null;
    attempted_targets?: number | null;
    completed_targets?: number | null;
    failed_targets?: number | null;
    skipped_targets?: number | null;
    [key: string]: unknown;
}

export interface MarketQuery {
    query_id: string;
    job_status: MarketQueryStatus;
    execution_mode: MarketExecutionMode | null;
    observation_plan: MarketObservationPlan;
    coverage_mode: MarketCoverageMode;
    target_accounting: MarketTargetAccounting | null;
    coverage_revision: number | null;
    freshness_at: string | null;
    budget_limited: boolean;
    unknowns: string[];
    source_results: MarketSourceResult[];
    evidence_count: number | null;
    records_observed: number | null;
    records_archived: number | null;
    provider_declared_total: number | null;
    pagination_complete: boolean | null;
    archive_complete: boolean | null;
    compliance_fresh: boolean | null;
    poll_after_seconds: number | null;
    submitted_at: string;
    completed_at: string | null;
    cost_micros: number | null;
    error: string | null;
}

export interface MarketSourceResult {
    capability: string;
    platform: 'meta' | 'tiktok' | 'reddit' | 'x';
    source_system: string;
    job_status: 'running' | 'ready' | 'partial' | 'failed';
    execution_mode: MarketExecutionMode | null;
    planned_slices: number;
    completed_slices: number;
    failed_slices: number;
    pending_slices: number;
    records_observed: number | null;
    records_archived: number | null;
    provider_declared_total: number | null;
    pagination_complete: boolean | null;
    freshness_at: string | null;
    archive_complete: boolean | null;
    compliance_fresh: boolean | null;
    repair_state: 'pending' | 'required' | 'not_required' | 'unavailable';
    unknowns: string[];
}

export interface MarketTargetIdentifierWrite {
    identifier_type: string;
    source_system: string;
    value: string;
    confidence: number;
}

export interface MarketWatchTargetWrite {
    target_type: MarketWatchTargetType;
    display_name: string;
    category: string;
    regions: string[];
    languages: string[];
    status: 'active' | 'paused';
    identifiers: MarketTargetIdentifierWrite[];
}

export interface MarketWatchTarget {
    id: string;
    target_type: MarketWatchTargetType;
    display_name: string;
    category: string;
    regions: string[];
    languages: string[];
    status: 'active' | 'paused' | 'retired';
    version: number;
    updated_at: string;
}

export interface MarketHeat {
    target_id: string;
    window: string;
    as_of: string | null;
    stage: MarketHeatStage;
    score: number | null;
    confidence: number | null;
    paid_signal_components: Record<string, unknown> | null;
    organic_signal_components: Record<string, unknown> | null;
    cross_source_components: Record<string, unknown> | null;
    coverage: Record<string, unknown> | null;
    baseline_version: string | null;
    unknowns: string[];
}

export interface MarketMonitorSummary {
    monitor: MarketMonitor;
    latest_run: MarketQuery | null;
    watch_target: MarketWatchTarget | null;
    heat: MarketHeat | null;
    heat_window: string;
    capabilities: MarketCapability[];
    data_quality: {
        status: 'unavailable' | 'running' | 'partial' | 'baseline_building' | 'ready';
        unknowns: string[];
    };
}

type IdFactory = () => string;

export class MarketIntelligenceResponseError extends Error {
    readonly code = 'MARKET_INTELLIGENCE_INVALID_RESPONSE';

    constructor(resource: string) {
        super(`Market Intelligence returned an invalid ${resource} response.`);
        this.name = 'MarketIntelligenceResponseError';
    }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value && typeof value === 'object' && !Array.isArray(value));

const validateCapability = (value: unknown): MarketCapability => {
    if (
        !isRecord(value)
        || typeof value.capability !== 'string'
        || !['meta', 'tiktok', 'reddit', 'x'].includes(String(value.platform))
        || typeof value.source_system !== 'string'
        || !['enabled', 'disabled', 'degraded'].includes(String(value.availability))
        || (value.execution_mode !== null
            && !['disabled', 'fake', 'provider'].includes(String(value.execution_mode)))
        || typeof value.reason_code !== 'string'
        || typeof value.rollout_stage !== 'string'
        || !isRecord(value.region_scope)
        || typeof value.region_scope.mode !== 'string'
        || !Array.isArray(value.region_scope.values)
        || !isRecord(value.language_scope)
        || typeof value.language_scope.mode !== 'string'
        || !Array.isArray(value.language_scope.values)
    ) {
        throw new MarketIntelligenceResponseError('capability');
    }
    return value as unknown as MarketCapability;
};

const validateMonitor = (value: unknown): MarketMonitor => {
    if (
        !isRecord(value)
        || value.contract_version !== '1.0.0'
        || typeof value.monitor_id !== 'string'
        || typeof value.name !== 'string'
        || (value.watch_target_id !== null && typeof value.watch_target_id !== 'string')
        || typeof value.version !== 'number'
        || !Array.isArray(value.advertiser_page_urls)
        || !Array.isArray(value.query_slices)
        || value.query_slices.some((slice) => !isRecord(slice) || typeof slice.query !== 'string')
        || typeof value.max_provider_records_per_run !== 'number'
        || !isRecord(value.current_plan)
        || typeof value.current_plan.planned_slices !== 'number'
    ) {
        throw new MarketIntelligenceResponseError('monitor');
    }
    return {
        ...(value as unknown as MarketMonitor),
        watch_target_id: typeof value.watch_target_id === 'string' ? value.watch_target_id : null,
    };
};

const validateMonitorPreview = (value: unknown): MarketMonitorPreview => {
    if (
        !isRecord(value)
        || !isRecord(value.normalized_monitor_spec)
        || !Number.isInteger(value.planned_slices)
        || Number(value.planned_slices) < 1
        || typeof value.max_provider_records !== 'number'
        || value.max_provider_records < 1
        || !Array.isArray(value.warnings)
        || value.warnings.some((warning) => typeof warning !== 'string')
    ) {
        throw new MarketIntelligenceResponseError('monitor preview');
    }
    return value as unknown as MarketMonitorPreview;
};

const validateSourceResult = (value: unknown): MarketSourceResult => {
    if (
        !isRecord(value)
        || typeof value.capability !== 'string'
        || !['meta', 'tiktok', 'reddit', 'x'].includes(String(value.platform))
        || typeof value.source_system !== 'string'
        || !['running', 'ready', 'partial', 'failed'].includes(String(value.job_status))
        || (value.execution_mode !== null
            && value.execution_mode !== 'fake'
            && value.execution_mode !== 'provider')
        || !['pending', 'required', 'not_required', 'unavailable'].includes(String(value.repair_state))
        || !['planned_slices', 'completed_slices', 'failed_slices', 'pending_slices']
            .every((key) => typeof value[key] === 'number')
        || (value.records_observed !== null && typeof value.records_observed !== 'number')
        || (value.records_archived !== null && typeof value.records_archived !== 'number')
        || (value.provider_declared_total !== undefined
            && value.provider_declared_total !== null
            && typeof value.provider_declared_total !== 'number')
        || (value.pagination_complete !== undefined
            && value.pagination_complete !== null
            && typeof value.pagination_complete !== 'boolean')
        || (value.freshness_at !== null && typeof value.freshness_at !== 'string')
        || (value.archive_complete !== null && typeof value.archive_complete !== 'boolean')
        || (value.compliance_fresh !== null && typeof value.compliance_fresh !== 'boolean')
        || !Array.isArray(value.unknowns)
    ) {
        throw new MarketIntelligenceResponseError('source result');
    }
    return {
        ...(value as unknown as MarketSourceResult),
        provider_declared_total:
            typeof value.provider_declared_total === 'number' ? value.provider_declared_total : null,
        pagination_complete:
            typeof value.pagination_complete === 'boolean' ? value.pagination_complete : null,
    };
};

const validateQuery = (value: unknown): MarketQuery => {
    const validStatus = isRecord(value)
        && ['accepted', 'running', 'ready', 'partial', 'failed'].includes(String(value.job_status));
    if (
        !isRecord(value)
        || typeof value.query_id !== 'string'
        || !validStatus
        || (value.execution_mode !== null
            && value.execution_mode !== 'fake'
            && value.execution_mode !== 'provider')
        || !isRecord(value.observation_plan)
        || typeof value.observation_plan.planned_slices !== 'number'
        || (value.evidence_count !== null && typeof value.evidence_count !== 'number')
        || !Array.isArray(value.unknowns)
        || !Array.isArray(value.source_results)
        || (value.completed_at !== null && typeof value.completed_at !== 'string')
        || (value.cost_micros !== null && typeof value.cost_micros !== 'number')
    ) {
        throw new MarketIntelligenceResponseError('query');
    }
    const sourceResults = value.source_results.map(validateSourceResult);
    return {
        ...(value as unknown as MarketQuery),
        source_results: sourceResults,
        records_observed: typeof value.records_observed === 'number' ? value.records_observed : null,
        records_archived: typeof value.records_archived === 'number' ? value.records_archived : null,
        provider_declared_total:
            typeof value.provider_declared_total === 'number' ? value.provider_declared_total : null,
        pagination_complete:
            typeof value.pagination_complete === 'boolean' ? value.pagination_complete : null,
        archive_complete: typeof value.archive_complete === 'boolean' ? value.archive_complete : null,
        compliance_fresh: typeof value.compliance_fresh === 'boolean' ? value.compliance_fresh : null,
    };
};

const validateWatchTarget = (value: unknown): MarketWatchTarget => {
    if (
        !isRecord(value)
        || typeof value.id !== 'string'
        || !['brand', 'product', 'ad_creative'].includes(String(value.target_type))
        || typeof value.display_name !== 'string'
        || !Array.isArray(value.regions)
        || !Array.isArray(value.languages)
        || !['active', 'paused', 'retired'].includes(String(value.status))
        || typeof value.version !== 'number'
        || typeof value.updated_at !== 'string'
    ) {
        throw new MarketIntelligenceResponseError('watch target');
    }
    return value as unknown as MarketWatchTarget;
};

const validateHeat = (value: unknown): MarketHeat => {
    if (
        !isRecord(value)
        || typeof value.target_id !== 'string'
        || typeof value.window !== 'string'
        || ![
            'baseline_building', 'insufficient_data', 'stable', 'emerging', 'rising', 'peaking', 'cooling',
        ].includes(String(value.stage))
        || (value.score !== null && typeof value.score !== 'number')
        || (value.confidence !== null && typeof value.confidence !== 'number')
        || !Array.isArray(value.unknowns)
        || (['baseline_building', 'insufficient_data'].includes(String(value.stage)) && value.score !== null)
    ) {
        throw new MarketIntelligenceResponseError('target heat');
    }
    return value as unknown as MarketHeat;
};

const validateRuntimeStatus = (value: unknown): MarketRuntimeStatus => {
    const validStatus = isRecord(value)
        && ['ready', 'blocked', 'unavailable'].includes(String(value.status));
    const validMode = isRecord(value)
        && (value.execution_mode === null
            || value.execution_mode === 'disabled'
            || value.execution_mode === 'fake'
            || value.execution_mode === 'provider');
    if (
        !isRecord(value)
        || !validStatus
        || !validMode
        || typeof value.can_run !== 'boolean'
        || typeof value.reason_code !== 'string'
        || value.reason_code.length === 0
        || value.can_run !== (value.status === 'ready')
        || (value.can_run && (value.execution_mode === null || value.execution_mode === 'disabled'))
    ) {
        throw new MarketIntelligenceResponseError('runtime status');
    }
    return value as unknown as MarketRuntimeStatus;
};

const validateMonitorSummary = (value: unknown): MarketMonitorSummary => {
    if (
        !isRecord(value)
        || typeof value.heat_window !== 'string'
        || !Array.isArray(value.capabilities)
        || !isRecord(value.data_quality)
        || typeof value.data_quality.status !== 'string'
        || !Array.isArray(value.data_quality.unknowns)
    ) {
        throw new MarketIntelligenceResponseError('monitor summary');
    }
    return {
        monitor: validateMonitor(value.monitor),
        latest_run: value.latest_run === null ? null : validateQuery(value.latest_run),
        watch_target: value.watch_target === null ? null : validateWatchTarget(value.watch_target),
        heat: value.heat === null ? null : validateHeat(value.heat),
        heat_window: value.heat_window,
        capabilities: value.capabilities.map(validateCapability),
        data_quality: value.data_quality as MarketMonitorSummary['data_quality'],
    };
};

const fallbackUuid = (): string => {
    const bytes = new Uint8Array(16);
    const cryptoApi = globalThis.crypto;
    if (cryptoApi?.getRandomValues) {
        cryptoApi.getRandomValues(bytes);
    } else {
        for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Math.floor(Math.random() * 256);
        }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const createMarketIntelligenceUuid = (): string => globalThis.crypto?.randomUUID?.() || fallbackUuid();

export const createMarketIntelligenceIdempotencyKey = (): string => `mi-web:${createMarketIntelligenceUuid()}`;

const requestConfig = (workspaceId: string, requestId: string, extra?: AxiosRequestConfig): AxiosRequestConfig => ({
    ...(extra || {}),
    headers: {
        ...(extra?.headers || {}),
        'X-Workspace-Id': workspaceId,
        'X-Request-Id': requestId,
        [PRODUCT_CONTEXT_HEADER]: BUSTLY_CORE_PRODUCT_CODE,
    },
});

export class MarketIntelligenceService {
    private readonly api: Api;
    private readonly idFactory: IdFactory;

    constructor(apiConfig: ApiConfig, idFactory: IdFactory = createMarketIntelligenceUuid) {
        this.api = new Api(apiConfig);
        this.idFactory = idFactory;
    }

    listMonitors(workspaceId: string, signal?: AbortSignal): Promise<MarketMonitor[]> {
        return this.api
            .get<unknown>('/api/market-intelligence/monitors', requestConfig(workspaceId, this.idFactory(), { signal }))
            .then((value) => {
                if (!Array.isArray(value)) throw new MarketIntelligenceResponseError('monitor list');
                return value.map(validateMonitor);
            });
    }

    getRuntimeStatus(workspaceId: string, signal?: AbortSignal): Promise<MarketRuntimeStatus> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/runtime-status',
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then(validateRuntimeStatus);
    }

    listCapabilities(workspaceId: string, signal?: AbortSignal): Promise<MarketCapability[]> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/capabilities',
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then((value) => {
                if (!Array.isArray(value)) throw new MarketIntelligenceResponseError('capability list');
                return value.map(validateCapability);
            });
    }

    putMonitor(
        workspaceId: string,
        monitorId: string,
        monitor: MarketMonitorWrite,
        version?: number,
        signal?: AbortSignal,
    ): Promise<MarketMonitor> {
        const extra: AxiosRequestConfig = { signal };
        if (version !== undefined) {
            extra.headers = { 'If-Match': `\"mi-v${version}\"` };
        }
        return this.api
            .put<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}`,
                monitor,
                requestConfig(workspaceId, this.idFactory(), extra),
            )
            .then(validateMonitor);
    }

    previewMonitor(
        workspaceId: string,
        monitor: MarketMonitorWrite,
        signal?: AbortSignal,
    ): Promise<MarketMonitorPreview> {
        return this.api
            .post<unknown>(
                '/api/market-intelligence/monitors/preview',
                monitor,
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then(validateMonitorPreview);
    }

    updateMonitorStatus(
        workspaceId: string,
        monitorId: string,
        enabled: boolean,
        version: number,
        signal?: AbortSignal,
    ): Promise<MarketMonitor> {
        return this.api
            .patch<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/status`,
                { enabled },
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    headers: { 'If-Match': `\"mi-v${version}\"` },
                }),
            )
            .then(validateMonitor);
    }

    getMonitorSummary(
        workspaceId: string,
        monitorId: string,
        heatWindow: MarketHeatWindow = '7d',
        signal?: AbortSignal,
    ): Promise<MarketMonitorSummary> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/summary`,
                requestConfig(workspaceId, this.idFactory(), { signal, params: { heat_window: heatWindow } }),
            )
            .then(validateMonitorSummary);
    }

    listMonitorSummaries(
        workspaceId: string,
        heatWindow: MarketHeatWindow = '7d',
        signal?: AbortSignal,
    ): Promise<MarketMonitorSummary[]> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/monitor-summaries',
                requestConfig(workspaceId, this.idFactory(), { signal, params: { heat_window: heatWindow } }),
            )
            .then((value) => {
                if (!Array.isArray(value)) throw new MarketIntelligenceResponseError('monitor summary list');
                return value.map(validateMonitorSummary);
            });
    }

    refreshMonitor(
        workspaceId: string,
        monitorId: string,
        idempotencyKey: string,
        signal?: AbortSignal,
    ): Promise<MarketQuery> {
        return this.api
            .post<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/refresh`,
                undefined,
                requestConfig(workspaceId, this.idFactory(), {
                    signal,
                    headers: { 'Idempotency-Key': idempotencyKey },
                }),
            )
            .then(validateQuery);
    }

    getQuery(workspaceId: string, queryId: string, signal?: AbortSignal): Promise<MarketQuery> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/queries/${encodeURIComponent(queryId)}`,
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then(validateQuery);
    }

    listRuns(
        workspaceId: string,
        monitorId: string,
        limit = 20,
        signal?: AbortSignal,
    ): Promise<MarketQuery[]> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/monitors/${encodeURIComponent(monitorId)}/runs`,
                requestConfig(workspaceId, this.idFactory(), { signal, params: { limit } }),
            )
            .then((value) => {
                if (!Array.isArray(value)) throw new MarketIntelligenceResponseError('run history');
                return value.map(validateQuery);
            });
    }

    listWatchTargets(workspaceId: string, signal?: AbortSignal): Promise<MarketWatchTarget[]> {
        return this.api
            .get<unknown>(
                '/api/market-intelligence/watch-targets',
                requestConfig(workspaceId, this.idFactory(), { signal }),
            )
            .then((value) => {
                if (!Array.isArray(value)) throw new MarketIntelligenceResponseError('watch target list');
                return value.map(validateWatchTarget);
            });
    }

    putWatchTarget(
        workspaceId: string,
        targetId: string,
        target: MarketWatchTargetWrite,
        version?: number,
        signal?: AbortSignal,
    ): Promise<MarketWatchTarget> {
        const extra: AxiosRequestConfig = { signal };
        if (version !== undefined) extra.headers = { 'If-Match': `\"${version}\"` };
        return this.api
            .put<unknown>(
                `/api/market-intelligence/watch-targets/${encodeURIComponent(targetId)}`,
                target,
                requestConfig(workspaceId, this.idFactory(), extra),
            )
            .then(validateWatchTarget);
    }

    getTargetHeat(
        workspaceId: string,
        targetId: string,
        window = '7d',
        signal?: AbortSignal,
    ): Promise<MarketHeat> {
        return this.api
            .get<unknown>(
                `/api/market-intelligence/watch-targets/${encodeURIComponent(targetId)}/heat`,
                requestConfig(workspaceId, this.idFactory(), { signal, params: { window } }),
            )
            .then(validateHeat);
    }
}

export const marketIntelligenceService = new MarketIntelligenceService({ baseURL: config.services.auth.apiHost });
